import type { PreparationJob, PreparationResult } from './jobs';
import { reportClientError } from '../../lib/clientErrors';

/**
 * Runs PDF preparation jobs one at a time, off the main thread when module workers are available.
 * Queued jobs whose signal aborts never start. An aborted running job's result is discarded; a job
 * that exceeds its deadline terminates the worker, since a timer cannot interrupt synchronous work.
 */
export interface PreparationExecutor { run(job: PreparationJob): Promise<PreparationResult>; terminate(): void }

const unavailable = (job: PreparationJob, message: string): PreparationResult => ({ ok: false, issues: [{ code: 'preparation_unavailable', ...(job.kind === 'document' ? { documentId: job.input.document.id } : {}), message }] });
const START_FAILURE = 'The form preparer could not start. Reload the page and try again; your answers are saved in this tab.';

export function workerExecutor(spawn = () => new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'pdf-preparation' })): PreparationExecutor {
  let worker: Worker | undefined, next = 0;
  let pending: { id: number; job: PreparationJob; resolve: (result: PreparationResult) => void } | undefined;
  const reset = () => { worker?.terminate(); worker = undefined; };
  const fail = () => { const job = pending; pending = undefined; reset(); job?.resolve(unavailable(job.job, START_FAILURE)); };
  return {
    run(job) {
      return new Promise(resolve => {
        try {
          if (!worker) {
            worker = spawn();
            worker.onmessage = (event: MessageEvent<{ id: number; result: PreparationResult }>) => {
              if (pending?.id !== event.data.id) return;
              const done = pending; pending = undefined; done.resolve(event.data.result);
            };
            worker.onerror = event => { event.preventDefault(); reportClientError('prepare: worker error', event.error ?? event.message, { source: event.filename, line: event.lineno }); fail(); };
            worker.onmessageerror = () => { reportClientError('prepare: worker message', new Error('Preparation result could not be read')); fail(); };
          }
          pending = { id: ++next, job, resolve };
          worker.postMessage({ id: pending.id, job });
        } catch (error) { reportClientError('prepare: start worker', error); pending = { id: -1, job, resolve }; fail(); }
      });
    },
    terminate() { const job = pending; pending = undefined; reset(); job?.resolve(unavailable(job.job, 'Preparation was cancelled.')); },
  };
}

/** Same jobs on the main thread, for environments without module workers (tests, very old browsers). */
export function inlineExecutor(): PreparationExecutor {
  return { run: async job => (await import('./jobs')).runPreparationJob(job), terminate() {} };
}

export class PdfPreparationService {
  private queue: { job: PreparationJob; signal?: AbortSignal; resolve: (r: PreparationResult) => void; reject: (e: unknown) => void }[] = [];
  private running = false;
  private executor?: PreparationExecutor;
  constructor(private readonly createExecutor: () => PreparationExecutor = () => typeof Worker === 'function' ? workerExecutor() : inlineExecutor(), private readonly timeoutMs = 60_000) {}

  prepare(job: PreparationJob, signal?: AbortSignal): Promise<PreparationResult> {
    if (signal?.aborted) return Promise.reject(abortError());
    return new Promise((resolve, reject) => { this.queue.push({ job, signal, resolve, reject }); void this.pump(); });
  }
  /** End of a registration: drop queued work and stop the worker. The font is fetched again from the HTTP cache. */
  dispose(): void {
    for (const item of this.queue.splice(0)) item.reject(abortError());
    this.executor?.terminate(); this.executor = undefined;
  }

  private async pump(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      for (let item = this.queue.shift(); item; item = this.queue.shift()) {
        if (item.signal?.aborted) { item.reject(abortError()); continue; }
        let executor: PreparationExecutor;
        try { executor = this.executor ??= this.createExecutor(); }
        catch (error) { reportClientError('prepare: start', error); item.resolve(unavailable(item.job, START_FAILURE)); continue; }
        let timer: ReturnType<typeof setTimeout> | undefined;
        const deadline = new Promise<PreparationResult>(resolve => { timer = setTimeout(() => {
          if (this.executor === executor) this.executor = undefined;
          executor.terminate();
          reportClientError('prepare: timeout', new Error(`Preparation took longer than ${this.timeoutMs} ms`), { kind: item!.job.kind });
          resolve({ ok: false, issues: [{ code: 'preparation_timeout', ...(item!.job.kind === 'document' ? { documentId: item!.job.input.document.id } : {}), message: 'Preparing this form took too long. Retry, or try a different browser or device.' }] });
        }, this.timeoutMs); });
        const run = executor.run(item.job).catch(error => { reportClientError('prepare: run', error); return unavailable(item!.job, START_FAILURE); });
        const result = await Promise.race([run, deadline]).finally(() => clearTimeout(timer));
        if (item.signal?.aborted) item.reject(abortError()); else item.resolve(result);
      }
    } finally { this.running = false; }
  }
}
const abortError = () => new DOMException('Preparation superseded', 'AbortError');

let shared: PdfPreparationService | undefined;
export const preparationService = () => shared ??= new PdfPreparationService();
